/**
 * Lap Zone Metrics Extraction
 *
 * Extracts detailed per-lap metrics from zone telemetry data.
 * These metrics form the basis for consensus and variance calculations.
 */

import { type ZoneDataPoint } from './ZoneDetection'
import {
  CONSENSUS_BRAKING_THRESHOLD_G,
  BRAKE_RELEASE_G,
  THROTTLE_G,
} from '../config/zoneDetection'

// ============================================================================
// Types
// ============================================================================

export interface LapZoneMetrics {
  entrySpeed: number
  minSpeed: number
  exitSpeed: number
  apexDistance: number
  brakingPointDistance: number | null
  brakeReleaseDistance: number | null
  peakBrakingG: number
  throttlePointDistance: number | null
  peakThrottleG: number
  coastingDistance: number
}

// ============================================================================
// Constants
// ============================================================================

export const G_CONVERSION = 9.81

export function toG(accelMs2: number): number {
  return accelMs2 / G_CONVERSION
}

// ============================================================================
// Metrics Extraction
// ============================================================================

/** Extract detailed metrics for a single lap's zone data */
export function extractLapZoneMetrics(
  lapData: ZoneDataPoint[],
  zoneStartDistance: number
): LapZoneMetrics {
  if (lapData.length === 0) {
    return createEmptyMetrics()
  }

  const { entrySpeed, exitSpeed, minSpeed, apexIdx, apexDistance } =
    extractSpeedMetrics(lapData, zoneStartDistance)
  const {
    brakingPointDistance,
    brakeReleaseDistance,
    lightBrakeReleaseDistance,
    peakBrakingG,
  } = extractBrakingMetrics(lapData, zoneStartDistance, apexIdx)
  const { throttlePointDistance, peakThrottleG } = extractThrottleMetrics(
    lapData,
    zoneStartDistance,
    apexIdx
  )
  // Use light threshold for coasting calculation (any braking counts)
  const coastingDistance = calculateCoastingDistance(
    lightBrakeReleaseDistance,
    throttlePointDistance
  )

  return {
    entrySpeed,
    minSpeed,
    exitSpeed,
    apexDistance,
    brakingPointDistance,
    brakeReleaseDistance,
    peakBrakingG,
    throttlePointDistance,
    peakThrottleG,
    coastingDistance,
  }
}

function createEmptyMetrics(): LapZoneMetrics {
  return {
    entrySpeed: 0,
    minSpeed: 0,
    exitSpeed: 0,
    apexDistance: 0,
    brakingPointDistance: null,
    brakeReleaseDistance: null,
    peakBrakingG: 0,
    throttlePointDistance: null,
    peakThrottleG: 0,
    coastingDistance: 0,
  }
}

function extractSpeedMetrics(
  lapData: ZoneDataPoint[],
  zoneStartDistance: number
) {
  const entrySpeed = lapData[0].speed
  const exitSpeed = lapData[lapData.length - 1].speed

  let minSpeed = Infinity
  let apexIdx = 0
  for (let i = 0; i < lapData.length; i++) {
    if (lapData[i].speed < minSpeed) {
      minSpeed = lapData[i].speed
      apexIdx = i
    }
  }
  const apexDistance = lapData[apexIdx].distance - zoneStartDistance

  return { entrySpeed, exitSpeed, minSpeed, apexIdx, apexDistance }
}

function extractBrakingMetrics(
  lapData: ZoneDataPoint[],
  zoneStartDistance: number,
  apexIdx: number
) {
  let brakingPointDistance: number | null = null
  let brakeReleaseDistance: number | null = null
  let lightBrakeReleaseDistance: number | null = null
  let peakBrakingG = 0

  // Find brake start and peak
  for (let i = 0; i < lapData.length; i++) {
    const gForce = toG(lapData[i].acceleration)
    if (
      gForce < CONSENSUS_BRAKING_THRESHOLD_G &&
      brakingPointDistance === null
    ) {
      brakingPointDistance = lapData[i].distance - zoneStartDistance
    }
    // Acceleration is pre-smoothed at the lap level by analyzeTrackZones
    if (lapData[i].acceleration < 0) {
      peakBrakingG = Math.max(peakBrakingG, Math.abs(gForce))
    }
  }

  // Find brake release - search backwards from apex
  // Light threshold (-0.15g) for coasting calculation
  // Heavy threshold (-0.3g) for the "off brakes" marker/variance
  for (let i = apexIdx; i >= 0; i--) {
    const gForce = toG(lapData[i].acceleration)
    if (
      gForce < CONSENSUS_BRAKING_THRESHOLD_G &&
      lightBrakeReleaseDistance === null
    ) {
      lightBrakeReleaseDistance = lapData[i].distance - zoneStartDistance
    }
    if (gForce < BRAKE_RELEASE_G && brakeReleaseDistance === null) {
      brakeReleaseDistance = lapData[i].distance - zoneStartDistance
    }
    if (lightBrakeReleaseDistance !== null && brakeReleaseDistance !== null) {
      break
    }
  }

  return {
    brakingPointDistance,
    brakeReleaseDistance,
    lightBrakeReleaseDistance,
    peakBrakingG,
  }
}

function extractThrottleMetrics(
  lapData: ZoneDataPoint[],
  zoneStartDistance: number,
  apexIdx: number
) {
  let throttlePointDistance: number | null = null
  let peakThrottleG = 0

  for (let i = apexIdx; i < lapData.length; i++) {
    const gForce = toG(lapData[i].acceleration)
    if (gForce > THROTTLE_G && throttlePointDistance === null) {
      throttlePointDistance = lapData[i].distance - zoneStartDistance
    }
    // Acceleration is pre-smoothed at the lap level by analyzeTrackZones
    if (lapData[i].acceleration > 0) {
      peakThrottleG = Math.max(peakThrottleG, gForce)
    }
  }

  return { throttlePointDistance, peakThrottleG }
}

function calculateCoastingDistance(
  brakeReleaseDistance: number | null,
  throttlePointDistance: number | null
): number {
  if (brakeReleaseDistance !== null && throttlePointDistance !== null) {
    return Math.max(0, throttlePointDistance - brakeReleaseDistance)
  }

  return 0
}
